import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Users as UsersIcon, UserPlus, Activity, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const Users: React.FC = () => {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => adminApiClient.getUserStats(),
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users', page, pageSize],
    queryFn: () => adminApiClient.getUsers({ page, page_size: pageSize }),
  });

  if (statsLoading || usersLoading) {
    return <Loading message="Loading users..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Users</h1>
        <p className="text-gray-400 mt-1">
          Manage and view all registered users
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats.total_users.toLocaleString()}
            icon={UsersIcon}
          />
          <StatCard
            title="New Today"
            value={stats.new_today.toLocaleString()}
            trend={{ value: stats.new_this_week, label: 'this week', isPositive: true }}
            icon={UserPlus}
          />
          <StatCard
            title="Active Users"
            value={stats.active_users.toLocaleString()}
            subtitle="Last 30 days"
            icon={Activity}
          />
          <StatCard
            title="Terms Accepted"
            value={stats.terms_accepted.toLocaleString()}
            icon={CheckCircle}
          />
        </div>
      )}

      {/* Users Table */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>All Users</CardTitle>
        </div>

        {users && users.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>User</TableCell>
                  <TableCell header>Clerk ID</TableCell>
                  <TableCell header>GitHub</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Accounts</TableCell>
                  <TableCell header>Analyses</TableCell>
                  <TableCell header>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.data.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{user.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.clerk_user_id ? (
                        <span className="font-mono text-xs text-gray-400">{user.clerk_user_id}</span>
                      ) : (
                        <span className="text-gray-600">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">@{user.github_username}</span>
                    </TableCell>
                    <TableCell>
                      {user.terms_accepted ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="warning">Pending ToS</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{user.account_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{user.analysis_count}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">
                        {format(new Date(user.created_at), 'MMM d, yyyy')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={users.page}
              totalPages={users.total_pages}
              totalItems={users.total}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No users found
          </div>
        )}
      </Card>
    </div>
  );
};

export default Users;
